'use client';

import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { BLACK_FRIDAY_CAMPAIGNS } from '@/constants/campaigns/black-friday';
import { BONUS_DAYS_CAMPAIGNS } from '@/constants/campaigns/bonus-days';
import { CHRISTMAS_CAMPAIGNS } from '@/constants/campaigns/christmas';
import { ENERGYJOINUP_CAMPAIGN_PAGE } from '@/constants/campaigns/energyjoinup';
import { GAMERS_DAYS_CAMPAIGNS } from '@/constants/campaigns/gamers-day';
import { REDIRECT_TRIAL_1DAY_PAGE } from '@/constants/campaigns/redirect-trial-1day-page';
import { HOME_HERO_V2_TOGGLE, HOME_SOFTWARE_SECTION_V2_TOGGLE } from '@/constants/feature-toggles/home-v2';
import { useMultipleFeatureToggles } from '@/hooks/use-feature-toggle';

interface FeatureToggleContextValue {
  gamersDay: boolean;
  blackFriday: boolean;
  christmas: boolean;
  bonusDays: boolean;
  redirectTrial1Day: boolean;
  energyJoinupCampaign: boolean;
  homeHeroV2: boolean;
  homeSoftwareSectionV2: boolean;
  isLoading: boolean;
}

export const FeatureToggleContext = createContext<FeatureToggleContextValue>({
  gamersDay: false,
  blackFriday: false,
  christmas: false,
  bonusDays: false,
  redirectTrial1Day: false,
  energyJoinupCampaign: false,
  homeHeroV2: false,
  homeSoftwareSectionV2: false,
  isLoading: false,
});

interface FeatureToggleProviderProps {
  children: ReactNode;
  initialValues?: {
    gamersDay?: boolean | null;
    blackFriday?: boolean | null;
    christmas?: boolean | null;
    bonusDays?: boolean | null;
    redirectTrial1Day?: boolean | null;
    energyJoinupCampaign?: boolean | null;
    homeHeroV2?: boolean | null;
    homeSoftwareSectionV2?: boolean | null;
  };
}

const FEATURE_TOGGLE_KEYS = [GAMERS_DAYS_CAMPAIGNS, BLACK_FRIDAY_CAMPAIGNS, CHRISTMAS_CAMPAIGNS, BONUS_DAYS_CAMPAIGNS, REDIRECT_TRIAL_1DAY_PAGE, ENERGYJOINUP_CAMPAIGN_PAGE, HOME_HERO_V2_TOGGLE, HOME_SOFTWARE_SECTION_V2_TOGGLE];

export function FeatureToggleProvider({ children, initialValues }: FeatureToggleProviderProps) {
  // The provider lives in `_app` and outlives every client-side navigation, while each page's
  // getServerSideProps resolves only its own toggles. A key the page did not resolve (`undefined`)
  // or the API could not answer (`null`) must stay absent here — coercing it to `false` would
  // overwrite the value another page resolved (SCRUM-2128).
  const serverValues = useMemo(() => {
    if (!initialValues) {
      return null;
    }
    const entries: Array<[string, boolean | null | undefined]> = [
      [GAMERS_DAYS_CAMPAIGNS, initialValues.gamersDay],
      [BLACK_FRIDAY_CAMPAIGNS, initialValues.blackFriday],
      [CHRISTMAS_CAMPAIGNS, initialValues.christmas],
      [BONUS_DAYS_CAMPAIGNS, initialValues.bonusDays],
      [REDIRECT_TRIAL_1DAY_PAGE, initialValues.redirectTrial1Day],
      [ENERGYJOINUP_CAMPAIGN_PAGE, initialValues.energyJoinupCampaign],
      [HOME_HERO_V2_TOGGLE, initialValues.homeHeroV2],
      [HOME_SOFTWARE_SECTION_V2_TOGGLE, initialValues.homeSoftwareSectionV2],
    ];
    return Object.fromEntries(entries.filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'));
  }, [initialValues]);

  const { toggles, isLoading } = useMultipleFeatureToggles({
    keys: FEATURE_TOGGLE_KEYS,
    autoFetch: !initialValues, // Only fetch if no server values provided
    serverValues,
  });

  const value: FeatureToggleContextValue = useMemo(
    () => ({
      gamersDay: toggles[GAMERS_DAYS_CAMPAIGNS] ?? initialValues?.gamersDay ?? false,
      blackFriday: toggles[BLACK_FRIDAY_CAMPAIGNS] ?? initialValues?.blackFriday ?? false,
      christmas: toggles[CHRISTMAS_CAMPAIGNS] ?? initialValues?.christmas ?? false,
      bonusDays: toggles[BONUS_DAYS_CAMPAIGNS] ?? initialValues?.bonusDays ?? false,
      redirectTrial1Day: toggles[REDIRECT_TRIAL_1DAY_PAGE] ?? initialValues?.redirectTrial1Day ?? false,
      energyJoinupCampaign: toggles[ENERGYJOINUP_CAMPAIGN_PAGE] ?? initialValues?.energyJoinupCampaign ?? false,
      homeHeroV2: toggles[HOME_HERO_V2_TOGGLE] ?? initialValues?.homeHeroV2 ?? false,
      homeSoftwareSectionV2: toggles[HOME_SOFTWARE_SECTION_V2_TOGGLE] ?? initialValues?.homeSoftwareSectionV2 ?? false,
      isLoading,
    }),
    [toggles, initialValues?.gamersDay, initialValues?.blackFriday, initialValues?.christmas, initialValues?.bonusDays, initialValues?.redirectTrial1Day, initialValues?.energyJoinupCampaign, initialValues?.homeHeroV2, initialValues?.homeSoftwareSectionV2, isLoading]
  );

  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
}

export function useFeatureToggles(): FeatureToggleContextValue {
  return useContext(FeatureToggleContext);
}
